<template>
  <Header
    navbar-type="goBack"
    show-help-btn
    @clicked="backAction"
    :screen-title="t('CL_WITHDRAWAL.IDENTITY_VERIFICATION')"
    hide-help-btn-text
    :help-icon="useAsset('img/credit-line/help-question-mark.svg')"
  />
  <IdentityVerification
    v-if="!validationFailed"
    :require-validation-response="requiredValidationResponse"
    :hide-close-button="hideCloseButton"
    :customerName="customerName"
    @next="onResponse"
    @on-validation-failed="onValidationFailed"
    :userFlow="route?.query?.userFlow as KycUserFlow"
  />
  <KycFailed v-if="validationFailed" />
</template>

<script setup lang="ts">
import { useRoute, useRouter } from "vue-router";
import { useStore } from "vuex";
import IdentityVerification from "@/apps/credit-line/components/Withdrawal/IdentityVerification.vue";
import { useCreditLineSummary } from "@/apps/credit-line/hooks/useCreditLineSummary";
import useAsset from "@/apps/credit-line/helpers/useAsset";
import Header from "@/shared-components/consumer/Header.vue";
import { useI18n } from "@/apps/credit-line/composables/useI18n";
import KycFailed from "@/apps/credit-line/components/KycValidations/KycFailed.vue";
import { onMounted, ref, computed } from "vue";
import { safeAndroidCall } from "@/apps/credit-line/helpers/safe-android-call.helper";
import { WithdrawMethod } from "@/apps/credit-line/types/withdrawMethod";
import { useSecureWithdrawal } from "@/apps/credit-line/hooks/useSecureWithdrawal";
import {
  AreValidationsRequiredResponse,
  NationalIdValidationResponse,
} from "../../models/IKycValidations";
import { KycUserFlow } from "../../enums/kycEnums";

const { t } = useI18n();
const route = useRoute();
const router = useRouter();
const store = useStore();
const { summary } = useCreditLineSummary();

const validationFailed = ref<boolean>(false);
const country = computed(() => store.getters["creditLine/getCountry"]);
const requiredValidationResponse = computed<AreValidationsRequiredResponse>(
  () => store.getters["creditLine/getWithdrawalValidationResponse"]
);
const needAmlSurvey = computed(
  () => store.getters["creditLine/getAmlSurveyData"]?.needSurvey || false
);
const withdrawalEstimation = computed(() => {
  return store.getters["creditLine/getWithdrawalEstimate"];
});
const bankTransfer = computed(
  () =>
    store.getters["creditLine/getSelectedWithdrawMethod"] ===
    WithdrawMethod.ONLINE
);

const { alertMessage, secureWithdrawal } = useSecureWithdrawal({
  bankTransfer: bankTransfer.value,
  t,
});

const hideCloseButton =
  route.query.hideCloseButton === "true" || route.query.hideCloseButton === "1";
const customerName = summary.value?.customerName || "";

const backAction = () => {
  if (validationFailed.value) {
    return safeAndroidCall("dismissWebView");
  }
  return router.back();
};

const onValidationFailed = () => {
  validationFailed.value = true;
  store.dispatch("creditLine/setValidationSuccess", false);
};

const onResponse = (result: NationalIdValidationResponse): void => {
  store.dispatch("creditLine/setCurpValidationResult", result);
  store.dispatch("creditLine/setValidationSuccess", true);

  if (needAmlSurvey.value) {
    router.replace({
      name: "AmlSurveyView",
      query: { country: country.value },
    });
    return;
  }

  // Secure Withdrawal
  secureWithdrawal({
    withdrawing: withdrawalEstimation.value.amount,
    withdrawalEstimation: withdrawalEstimation.value,
    onError: () => {
      router.replace({
        name: "CreditLineApiErrorDisplay",
        query: {
          title: t("ERROR_SOMETHING_GOES_WRONG"),
          description: alertMessage.value,
          returnHomeOnTryAgain: "true",
        },
      });
    },
  });
};

onMounted(() => {
  if (
    requiredValidationResponse.value &&
    !requiredValidationResponse.value?.wasSuccessful
  ) {
    onValidationFailed();
  }

  safeAndroidCall("hideNav");
});
</script>
